import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useProposalCarriedOverNegotiation } from "work/proposals/negotiation/proposal-carried-over-negotiation-context";
import type { ProposalActivityCoverage } from "work/proposals/negotiation/proposal-activity";
import ProposalTimelinePanel from "work/proposals/negotiation/proposal-timeline-panel";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

// The negotiation this revision continues, below the one it is: they are two negotiations, and a
// change made to the terms being revised is not a change to the revision. Muted and ruled off, so it
// reads as the record behind the field rather than as more of its current history.
const CarriedOverSection = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(1.5),
  opacity: 0.85,
  paddingTop: theme.spacing(1),
}));

const CarriedOverHeading = styled(Typography)(({ theme }) => ({
  display: "block",
  paddingBottom: theme.spacing(0.5),
})) as typeof Typography;

type ProposalCarriedOverFieldHistoryProps = {
  /** The field whose earlier history to show. Leave it out when `activity` is given. */
  field?: ProposalField;
  /** Shows the earlier negotiation's activity rather than one field's history (#5648). */
  activity?: ProposalActivityCoverage;
  /** The item's identity in the stream, as the control above it passes it to its own history. */
  collectionItemId?: string;
  /** Whether the control hosting it is open: a closed one asks for nothing. */
  enabled: boolean;
  className?: string;
};

/**
 * One field's history in the negotiation a revision continues, under "Before this revision" (#5567).
 *
 * Drawn by both of a field's history controls — the read-only popover and the revert menu — beneath
 * their own entries, and draws nothing on a proposal that revises nothing (see
 * `ProposalCarriedOverNegotiationContext`). The page's Activity popover draws it too, for the earlier
 * negotiation's activity that no field shows (#5648).
 *
 * A collection item is looked for there by the same identity it has here: a revision copies its
 * fee-schedule categories and documents with their ids, and a team member is their user in both.
 *
 * Read-only, never badged and never marked seen: an agreed negotiation's changes are not this one's
 * unread ones, and none of them can be reverted to from here — they are history of the terms this
 * revision opened on, not of anything written in it.
 */
export default function ProposalCarriedOverFieldHistory(props: Readonly<ProposalCarriedOverFieldHistoryProps>) {
  const { field, activity, collectionItemId, enabled, className } = props;
  const carriedOver = useProposalCarriedOverNegotiation();

  if (!carriedOver) return null;

  return (
    <CarriedOverSection className={className} data-testid="carried-over-field-history">
      <CarriedOverHeading variant="subtitle2" component="h3" color="text.secondary">
        Before this revision
      </CarriedOverHeading>
      <ProposalTimelinePanel
        negotiationId={carriedOver.negotiationId}
        viewerScope={carriedOver.viewerScope}
        field={field}
        activity={activity}
        collectionItemId={collectionItemId}
        enabled={enabled}
        includeComments={false}
        period="beforeRevision"
      />
    </CarriedOverSection>
  );
}
